import type {JobRequirement} from './types'

/**
 * Reorder `items` to match `orderIds`, appending any items whose ids were not listed.
 */
export function orderByStableIds<T extends {id: string}>(items: T[], orderIds: string[]): T[] {
    const byId = new Map(items.map((item) => [item.id, item]))
    const ordered: T[] = []
    const seen = new Set<string>()

    for (const id of orderIds) {
        const item = byId.get(id)
        if (item) {
            ordered.push(item)
            seen.add(item.id)
        }
    }

    for (const item of items) {
        if (!seen.has(item.id)) {
            ordered.push(item)
        }
    }

    return ordered
}

/**
 * Keep requirement list order stable across board refetches so the assignment
 * modal does not reshuffle sections when fulfillment changes.
 */
export function preserveRequirementOrder(
    prior: JobRequirement[] | undefined,
    next: JobRequirement[],
): JobRequirement[] {
    if (prior == null || prior.length === 0) {
        return next
    }

    return orderByStableIds(
        next,
        prior.map((requirement) => requirement.id),
    )
}

/**
 * Survives assignment-panel remounts while the same job modal stays conceptually open.
 * Cleared when the modal closes (`releaseFrozenRequirementOrder`).
 */
const frozenRequirementOrderByJobId = new Map<string, string[]>()

/** Remember (or extend) the first-seen requirement id order for an open job. */
export function freezeRequirementOrder(jobId: string, requirements: JobRequirement[]): string[] {
    const existing = frozenRequirementOrderByJobId.get(jobId)
    if (existing) {
        for (const requirement of requirements) {
            if (!existing.includes(requirement.id)) {
                existing.push(requirement.id)
            }
        }
        return existing
    }

    const orderIds = requirements.map((requirement) => requirement.id)
    frozenRequirementOrderByJobId.set(jobId, orderIds)
    return orderIds
}

/** Drop frozen order when the job modal closes. */
export function releaseFrozenRequirementOrder(jobId: string): void {
    frozenRequirementOrderByJobId.delete(jobId)
}

/** Test helper — clear all frozen orders between cases. */
export function clearFrozenRequirementOrders(): void {
    frozenRequirementOrderByJobId.clear()
}

/** Apply the frozen open-job order to a requirements list. */
export function applyFrozenRequirementOrder(
    jobId: string,
    requirements: JobRequirement[],
): JobRequirement[] {
    return orderByStableIds(requirements, freezeRequirementOrder(jobId, requirements))
}
